import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxAllModule } from '@syncfusion/ej2-angular-inputs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StandardReportService } from '../service/standard-report.service';
import { UserSwitcherService } from '../service/user-switcher.service';
import { WeeklyReportSessionService } from '../service/weekly-report-session.service';

/**
* OTP verification dialog used to gate access to the weekly report — handles sending, verifying and resending OTPs and shows success / error feedback to the user.
* @component
* @selector app-weekly-report-otp-verification
* @example
*   <app-weekly-report-otp-verification></app-weekly-report-otp-verification>
* @input none - This component does not accept any @Input() properties. It reads user and token information from sessionStorage/localStorage as needed.
* @output none - This component does not emit any @Output() events. It navigates internally to the weekly report route upon successful verification.
*
* Description:
*   The WeeklyReportOtpVerificationComponent provides a UI and logic for:
*   - Sending an OTP to the currently authenticated user's registered email for access to the weekly report.
*   - Verifying the entered OTP and, on success, navigating the user to the weekly report page.
*   - Resending an OTP with a disable timer to prevent rapid repeated requests.
*
* Key properties:
*   - otp: string — model for the OTP input.
*   - errorMessage: string — stores error messages to display to the user.
*   - successMessage: string — stores success messages to display to the user.
*   - isResendDisabled: boolean — indicates if the "resend" action is currently disabled (cooldown).
*   - resendTimer: number — countdown seconds remaining until resend becomes enabled.
*   - isOtpSent: boolean — indicates whether an OTP has been successfully sent.
*   - isSendingOtp: boolean — prevents duplicate send requests while one is in progress.
*
* Key methods:
*   - sendOtp(): void
*       Initiates a request (via StandardReportService) to send an OTP to the user's registered email.
*       Manages isSendingOtp, successMessage and errorMessage. On success sets isOtpSent and starts the resend timer.
*       Reads userId, token and customerId from localStorage for the request parameters.
*
*   - verifyOtp(): void
*       Validates the entered OTP locally (presence and length) then calls UserSwitcherService.verifyOTP.
*       On successful verification navigates to '/weeklyreport' via Router and sets successMessage.
*       On failure sets errorMessage based on server response or a generic message.
*
*   - resendOtp(): void
*       If not disabled, starts a 30-second countdown disabling further resends and calls UserSwitcherService.generateOTPUserSwitcher to request a new OTP.
*       Reads userId and token from sessionStorage for the request parameters. Updates successMessage or errorMessage based on response.
*
*   - startResendTimer(): void
*       Helper that sets isResendDisabled=true and begins a 30-second interval that decrements resendTimer each second and re-enables resend when it reaches zero.
*
* Lifecycle hooks:
*   - ngOnInit(): void
*       (Initialization placeholder — currently no special initialization logic required.)
*
*   - ngOnDestroy(): void
*       Ensures any active interval timers are cleared to avoid memory leaks when the component is destroyed.
*
* Notes and behavior:
*   - The component relies on StandardReportService and UserSwitcherService for backend OTP operations and Router for navigation.
*   - Tokens and identifiers are retrieved from localStorage/sessionStorage — ensure these values are present in the environment where the component runs.
*   - UI messages and button states are managed via the public properties listed above so templates can bind to them for user feedback.
*/
@Component({
  selector: 'app-weekly-report-otp-verification',
  standalone: true,
  imports: [CommonModule, FormsModule, TextBoxAllModule, ButtonModule, TranslateModule],
  templateUrl: './weekly-report-otp-verification.component.html',
  styleUrl: './weekly-report-otp-verification.component.css'
})
export class WeeklyReportOtpVerificationComponent implements OnInit, OnDestroy {
  otp: string = '';
  errorMessage: string = '';
  successMessage: string = '';
  isResendDisabled: boolean = false;
  resendTimer: number = 0;
  private timerInterval: any;
  isOtpSent: boolean = false;
  isSendingOtp: boolean = false;

  constructor(
    private router: Router,
    private userSwitcherService: UserSwitcherService,
    private standardReportService: StandardReportService,
    private translate: TranslateService,
    private weeklySession: WeeklyReportSessionService,
  ) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }


  /**
   * The report-session values every OTP call needs. Returns `null` (and sets the error
   * message) when the page was reached without the mail-link bootstrap that mints the
   * report token: a direct navigation, or a reload after the token was cleared. Without
   * this guard those requests went out with user id 0 and an empty bearer token.
   * @returns The request params and bearer token, or `null` when there is no report session.
   */
  private otpRequest(): { params: { user_id: number }; token: string } | null {
    const userId = this.weeklySession.userId();
    const token = this.weeklySession.token();
    if (!token || userId <= 0) {
      this.errorMessage = this.translate.instant('app.weekly_report_auth_redirect.unable_to_verify_access');
      return null;
    }
    return { params: { user_id: userId }, token };
  }

  /**
  * Sends an OTP to the user's registered email to grant access to the weekly report, updating component state (isSendingOtp, isOtpSent, errorMessage, successMessage) and starting the resend timer on success.
  * @example
  * endOtp()
  * void
  * @returns {void} Performs the OTP send action and updates component state; does not return a value.
  **/
  sendOtp(): void {
    if (this.isSendingOtp) return;

    this.errorMessage = '';
    this.successMessage = '';

    const request = this.otpRequest();
    if (!request) return;
    this.isSendingOtp = true;

    this.standardReportService.resendOTPToGetAccessToWeeklyReport(request.params, request.token).subscribe({
      next: (response: any) => {
        this.isSendingOtp = false;

        if (response.status === 200 && response.body?.success) {
          this.isOtpSent = true;
          this.successMessage = this.translate.instant('app.weekly_report_otp_verification.otp_sent_to_registered_email');
          this.startResendTimer(); // optional
        } else {
          this.errorMessage = this.translate.instant('app.weekly_report_otp_verification.failed_to_send_otp');
        }
      },
      error: () => {
        this.isSendingOtp = false;
        this.errorMessage = this.translate.instant('app.weekly_report_otp_verification.failed_to_send_otp');
      }
    });
  }

  /**
  * Verify the OTP entered by the user: validate input, call the verification API, update success/error messages, and navigate on success.
  * @example
  * verifyOtp()
  * void
  * @param {{void}} {{none}} - This method does not accept any arguments.
  * @returns {{void}} No return value; performs side effects (updates component messages and navigates to '/weeklyreport' on success).
  **/
  verifyOtp(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.otp || this.otp.trim() === '') {
      this.errorMessage = this.translate.instant('app.weekly_report_otp_verification.please_enter_otp');
      return;
    }

    const request = this.otpRequest();
    if (!request) return;

    const params = {
      ...request.params,
      otp: Number(this.otp)
    };

    this.standardReportService.verifyOTPToGetAccessToWeeklyReport(params, request.token).subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.body?.success) {
          this.successMessage = this.translate.instant('app.weekly_report_otp_verification.otp_verified_successfully');
          this.router.navigate(['/weeklyreport']);
        } else {
          this.errorMessage = this.translate.instant('app.weekly_report_otp_verification.invalid_otp_try_again');
        }
      },
      error: (error: any) => {
        this.errorMessage = error.error?.message || this.translate.instant('app.weekly_report_otp_verification.invalid_otp_try_again');
      }
    });
  }

  /**
  * Sends a request to resend the OTP for the current user, starts a 30-second resend cooldown, and manages success/error messages.
  * @example
  * esendOtp()
  * undefined
  * @param {void} none - No parameters.
  * @returns {void} Triggers OTP resend and updates component state (messages and resend cooldown) without returning a value.
  **/
  resendOtp(): void {
    if (this.isResendDisabled) return;

    this.isResendDisabled = true;
    this.resendTimer = 30;
    this.errorMessage = '';
    this.successMessage = '';

    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.isResendDisabled = false;
        clearInterval(this.timerInterval);
      }
    }, 1000);

    const request = this.otpRequest();
    if (!request) return;

    this.standardReportService.resendOTPToGetAccessToWeeklyReport(request.params, request.token).subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.body?.success) {
          this.successMessage = this.translate.instant('app.weekly_report_otp_verification.otp_resent_to_email');
        }
      },
      error: (error: any) => {
        this.errorMessage = this.translate.instant('app.weekly_report_otp_verification.failed_to_resend_otp');
      }
    });
  }

  /**
  * Starts a 30-second resend cooldown timer, disables the resend action while counting down, and re-enables it when finished.
  * @example
  * tartResendTimer()
  * // void (no return value)
  * @returns {void} No return value.
  **/
  startResendTimer(): void {
    this.isResendDisabled = true;
    this.resendTimer = 60;

    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) {
        this.isResendDisabled = false;
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }
}
